import { catalogKeySet } from '../permission-catalog/permission-catalog';
import {
  effectivePermissions,
  hasPermission,
  type PermissionContext,
} from '../has-permission/has-permission';
import { type PermissionKey } from '../permission-key/permission-key';

/**
 * The required keys this context does **not** authorize.
 *
 * `[]` means fully covered. The keys are returned rather than a boolean because
 * every caller reports them: the role editor needs to say *which* permission an
 * operator may not hand out, and "insufficient authority" with no list is a
 * refusal nobody can act on.
 */
export function uncoveredPermissions(
  context: PermissionContext,
  required: readonly PermissionKey[],
  known: ReadonlySet<string> = context.known ?? catalogKeySet(),
): PermissionKey[] {
  return required.filter((key) => !hasPermission({ ...context, known }, key));
}

/**
 * Whether `actor`'s effective authority fully **contains** `subject`'s.
 *
 * This is the confinement half of access control, and it is composed entirely
 * from `hasPermission` / `effectivePermissions` — never a second evaluator, never
 * a set-difference over raw grant arrays (N22). Two rules depend on it:
 *
 * - **authoring containment** — an actor may not grant what they do not hold
 *   (`uncoveredPermissions` above);
 * - **target confinement** — an actor may not mutate an account that holds
 *   authority they lack, so a weak staff user can never suspend, ban, re-role or
 *   log out a stronger one.
 *
 * Three consequences of expressing it this way, each of them load-bearing:
 *
 * 1. a **member** has zero catalog permissions (N8), so their effective set is
 *    empty and every actor contains them — member moderation keeps working with
 *    no "is this a member" branch anywhere in the policy;
 * 2. **denies count on both sides**, because `hasPermission` matches them
 *    bidirectionally: a subject carved out of billing demands less, and an actor
 *    carved out of billing contains less. An `includes`-based comparison would
 *    get both backwards;
 * 3. it is **reflexive** — equal authority contains itself. That is required,
 *    not incidental: the founder rule below only makes sense if a super admin can
 *    act on another super admin, and the owner's rule is that an actor may not
 *    act on someone STRONGER, not on an equal.
 *
 * `super_admin` is asymmetric on purpose: only a super admin contains a super
 * admin. The role bypasses denies as well as grants, so it is strictly more than
 * the global `manage` wildcard, and a `manage` holder who could strip it would be
 * reaching past their own ceiling.
 */
export function containsAccess(
  actor: PermissionContext,
  subject: PermissionContext,
  known: ReadonlySet<string> = actor.known ?? catalogKeySet(),
): boolean {
  if (subject.isSuperAdmin === true) {
    return actor.isSuperAdmin === true;
  }

  if (actor.isSuperAdmin === true) {
    return true;
  }

  return uncoveredPermissions(actor, effectivePermissions(subject, known), known).length === 0;
}
